import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Save,
    Landmark,
    Zap,
    Wallet,
    ShieldCheck,
    ArrowLeft,
} from "lucide-react";

const EMPTY = {
    method: "bank",
    account_name: "",
    bank_name: "",
    account_number: "",
    routing_code: "",
    swift_bic: "",
    currency: "hkd",
    stripe_connect_id: "",
    paypal_email: "",
    tax_id: "",
    notes: "",
};

const METHODS = [
    {
        v: "bank",
        label: "Bank transfer",
        sub: "We wire your commission monthly to the account below.",
        icon: Landmark,
    },
    {
        v: "stripe_connect",
        label: "Stripe Connect",
        sub: "Same-day automatic payouts via your Stripe Connect account.",
        icon: Zap,
    },
    {
        v: "paypal",
        label: "PayPal",
        sub: "Simple monthly payout to a PayPal email of your choice.",
        icon: Wallet,
    },
];

export default function AffiliatePayoutDetails() {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const set = (k) => (e) => {
        const v = e && e.target ? e.target.value : e;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const load = () =>
        api
            .get("/affiliate/payout-details")
            .then((r) => {
                setForm({ ...EMPTY, ...(r.data || {}) });
                setSavedAt(r.data?.updated_at || null);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));

    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            Object.keys(payload).forEach((k) => {
                if (typeof payload[k] === "string") payload[k] = payload[k].trim();
            });
            // Only send fields relevant to the selected method to keep the
            // stored doc clean and the admin summary tidy.
            if (payload.method !== "bank") {
                payload.account_name = payload.bank_name = payload.account_number =
                    payload.routing_code = payload.swift_bic = "";
            }
            if (payload.method !== "stripe_connect") {
                payload.stripe_connect_id = "";
            }
            if (payload.method !== "paypal") {
                payload.paypal_email = "";
            }
            const { data } = await api.put("/affiliate/payout-details", payload);
            setForm({ ...EMPTY, ...data });
            setSavedAt(data.updated_at);
            toast.success("Payout details saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-10 text-navy-500">Loading…</div>;

    return (
        <>
            <PageHeader
                eyebrow="Payouts"
                title="Payout details"
                description="Tell us where to send your affiliate commissions. Encrypted at rest and only visible to Perx finance."
                testid="affiliate-payout-header"
                action={
                    <Link to="/affiliate/payments">
                        <Button
                            variant="outline"
                            className="rounded-full h-11 border-navy-200 text-navy-700"
                        >
                            <ArrowLeft size={14} className="mr-1.5" />
                            Back to payments
                        </Button>
                    </Link>
                }
            />

            <div className="px-6 lg:px-12 py-8 max-w-3xl">
                <form
                    onSubmit={submit}
                    data-testid="affiliate-payout-form"
                    className="rounded-2xl bg-white shadow-soft border border-navy-100 p-8 space-y-6"
                >
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                            Method
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            {METHODS.map((opt) => {
                                const Icon = opt.icon;
                                return (
                                    <label
                                        key={opt.v}
                                        data-testid={`aff-payout-method-${opt.v}`}
                                        className={`cursor-pointer rounded-xl border-2 p-5 transition-colors ${
                                            form.method === opt.v
                                                ? "border-gold-500 bg-gold-500/5"
                                                : "border-navy-100/70 hover:border-navy-100"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="method"
                                            value={opt.v}
                                            checked={form.method === opt.v}
                                            onChange={() =>
                                                setForm((f) => ({ ...f, method: opt.v }))
                                            }
                                            className="sr-only"
                                        />
                                        <div className="flex items-center gap-2 text-navy-700 font-medium mb-1">
                                            <Icon size={16} className="text-gold-600" />
                                            {opt.label}
                                        </div>
                                        <div className="text-xs text-navy-500 leading-relaxed">
                                            {opt.sub}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {form.method === "bank" && (
                        <>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Account holder name" required>
                                    <Input
                                        required
                                        value={form.account_name}
                                        onChange={set("account_name")}
                                        data-testid="aff-payout-account-name"
                                        placeholder="Aria Ochoa"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                    />
                                </Field>
                                <Field label="Bank name">
                                    <Input
                                        value={form.bank_name}
                                        onChange={set("bank_name")}
                                        data-testid="aff-payout-bank-name"
                                        placeholder="HSBC"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                    />
                                </Field>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Account number" required>
                                    <Input
                                        required
                                        value={form.account_number}
                                        onChange={set("account_number")}
                                        data-testid="aff-payout-account-number"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 font-mono"
                                    />
                                </Field>
                                <Field label="Currency">
                                    <Input
                                        value={form.currency}
                                        onChange={set("currency")}
                                        data-testid="aff-payout-currency"
                                        placeholder="hkd"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 uppercase"
                                    />
                                </Field>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Routing / branch code">
                                    <Input
                                        value={form.routing_code}
                                        onChange={set("routing_code")}
                                        data-testid="aff-payout-routing-code"
                                        placeholder="004"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 font-mono"
                                    />
                                </Field>
                                <Field label="SWIFT / BIC">
                                    <Input
                                        value={form.swift_bic}
                                        onChange={set("swift_bic")}
                                        data-testid="aff-payout-swift"
                                        placeholder="HSBCHKHHHKH"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 font-mono"
                                    />
                                </Field>
                            </div>
                        </>
                    )}

                    {form.method === "stripe_connect" && (
                        <Field label="Stripe Connect account ID" required>
                            <Input
                                required
                                value={form.stripe_connect_id}
                                onChange={set("stripe_connect_id")}
                                data-testid="aff-payout-stripe-id"
                                placeholder="acct_1Abc…"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 font-mono"
                            />
                            <p className="text-[11px] text-navy-500 mt-1.5">
                                Copy the connected account ID from your Stripe dashboard.
                                Commissions settle same-day via Stripe Payouts.
                            </p>
                        </Field>
                    )}

                    {form.method === "paypal" && (
                        <Field label="PayPal email" required>
                            <Input
                                type="email"
                                required
                                value={form.paypal_email}
                                onChange={set("paypal_email")}
                                data-testid="aff-payout-paypal-email"
                                placeholder="you@paypal.com"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                            />
                        </Field>
                    )}

                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Tax ID / TIN (optional)">
                            <Input
                                value={form.tax_id}
                                onChange={set("tax_id")}
                                data-testid="aff-payout-tax-id"
                                placeholder="For W-8BEN / regional tax reporting"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 font-mono"
                            />
                        </Field>
                    </div>

                    <Field label="Notes for our finance team">
                        <Textarea
                            rows={3}
                            value={form.notes}
                            onChange={set("notes")}
                            data-testid="aff-payout-notes"
                            placeholder="Any special payout instructions?"
                            className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 resize-none"
                        />
                    </Field>

                    <div className="rounded-xl bg-cream border border-navy-100 p-4 flex items-start gap-3">
                        <ShieldCheck
                            size={18}
                            className="text-gold-600 flex-shrink-0 mt-0.5"
                            strokeWidth={1.7}
                        />
                        <div className="text-xs text-navy-500 leading-relaxed">
                            Your details are encrypted at rest and only visible to
                            Perx finance admins. Commissions run on the 5th of every
                            month covering the previous calendar month&apos;s converted
                            referrals.
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
                        {savedAt && (
                            <div className="text-xs text-navy-500">
                                Last updated{" "}
                                {new Date(savedAt).toLocaleString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="aff-payout-save"
                            className="ml-auto rounded-full bg-navy-700 hover:bg-navy-800 text-cream h-11 px-6"
                        >
                            <Save size={15} className="mr-2" />
                            {saving ? "Saving…" : "Save payout details"}
                        </Button>
                    </div>
                </form>
            </div>
        </>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            {children}
        </div>
    );
}
